'use client';

/**
 * NeonSection Component
 *
 * A darker nested section designed to be used inside NeonCard.
 * Creates visual hierarchy with a semi-transparent dark background.
 *
 * @example
 * ```tsx
 * <NeonCard accent="purple">
 *   <Typography variant="h6">Title</Typography>
 *   <NeonSection>
 *     <Typography>Nested content with darker background</Typography>
 *   </NeonSection>
 * </NeonCard>
 * ```
 */

import Box, { type BoxProps } from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import type React from 'react';
import { forwardRef } from 'react';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface NeonSectionProps extends Omit<BoxProps, 'component'> {
  /**
   * Padding size
   * @default 'md'
   */
  padding?: 'none' | 'sm' | 'md' | 'lg';

  /**
   * Border radius
   * @default 8
   */
  borderRadius?: number | string;

  /**
   * Background opacity (0-1)
   * @default 0.2 (dark mode) / 0.05 (light mode)
   */
  opacity?: number;

  /**
   * Margin top in theme spacing units
   * @default 2
   */
  marginTop?: number;

  /**
   * Children
   */
  children?: React.ReactNode;
}

// ============================================================================
// Constants
// ============================================================================

const paddingMap = {
  none: 0,
  sm: 1.5,
  md: 2,
  lg: 3,
} as const;

// ============================================================================
// Styled Components
// ============================================================================

interface StyledNeonSectionProps {
  $padding: number;
  $borderRadius: number | string;
  $background: string;
  $marginTop: number;
}

const StyledNeonSection = styled(
  Box,
  transientOptions,
)<StyledNeonSectionProps>(({ theme, $padding, $borderRadius, $background, $marginTop }) => ({
  background: $background,
  borderRadius: typeof $borderRadius === 'number' ? `${$borderRadius}px` : $borderRadius,
  padding: theme.spacing($padding),
  marginTop: theme.spacing($marginTop),
}));

// ============================================================================
// Component
// ============================================================================

export const NeonSection = forwardRef<HTMLDivElement, NeonSectionProps>(
  ({ children, padding = 'md', borderRadius = 8, opacity, marginTop = 2, sx, ...props }, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    // Default opacity is mode-aware; an explicit `opacity` prop applies unmodified to both modes.
    const resolvedOpacity = opacity ?? (isDark ? 0.2 : 0.05);
    const background = `rgba(0, 0, 0, ${resolvedOpacity})`;

    return (
      <StyledNeonSection
        ref={ref}
        $padding={paddingMap[padding]}
        $borderRadius={borderRadius}
        $background={background}
        $marginTop={marginTop}
        sx={sx}
        {...props}
      >
        {children}
      </StyledNeonSection>
    );
  },
);

NeonSection.displayName = 'NeonSection';

export default NeonSection;
